/**
 * Account Deactivated Landing Page
 *
 * Shown to a user whose `user_profiles.is_active` is false. Reached two ways:
 *  1. AuthContext detects `isActive === false` on a fresh login / session
 *     restore, calls `logout()`, and navigates here.
 *  2. A direct link, typically /account-deactivated provided by support so
 *     a user landing on the public site after a logout has somewhere to
 *     read the explanation again.
 *
 * The page is intentionally public (no auth required) — by the time we get
 * here the user has been logged out, so we can't pull their name from the
 * store. Showing only the contact-support message keeps the UX honest
 * without leaking which email belongs to a deactivated account.
 */

import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { ShieldAlert, Mail, MessageSquareText } from 'lucide-react';
import { PublicHeader } from '../../components/layout/PublicHeader';

interface LocationState {
  email?: string;
  reason?: string;
}

const SUPPORT_EMAIL = 'support@highcallrodeo.com';

export default function AccountDeactivatedPage() {
  // Email + reason can come either via router state (in-app navigation) or
  // a query param (the AuthContext hard-redirect after logout uses
  // ?email=…&reason=… because window.location.assign drops React Router
  // state).
  const location = useLocation();
  const state = location.state as LocationState | null;
  const [searchParams] = useSearchParams();
  const email = state?.email || searchParams.get('email') || undefined;
  const reasonRaw = state?.reason ?? searchParams.get('reason');
  const reason = reasonRaw ? reasonRaw.trim() : '';

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <PublicHeader />
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="rounded-lg border bg-card text-card-foreground shadow-sm p-6 text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-destructive/10 mb-4">
              <ShieldAlert className="h-8 w-8 text-destructive" />
            </div>

            <h1 className="text-2xl font-bold text-foreground mb-2">
              Account Deactivated
            </h1>

            <p className="text-sm text-muted-foreground mb-6">
              This account has been deactivated by an administrator. You will
              not be able to register for events, change profile information,
              or make payments while the account is inactive.
            </p>

            {email && (
              <p
                className="text-sm font-medium text-foreground mb-4 break-all"
                data-testid="deactivated-account-email"
              >
                {email}
              </p>
            )}

            {reason && (
              <div
                className="rounded-md border border-destructive/40 bg-destructive/10 p-4 mb-4 text-left"
                data-testid="deactivation-reason"
              >
                <div className="flex items-start gap-3">
                  <MessageSquareText className="h-5 w-5 text-destructive mt-0.5 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-foreground">
                      Reason from the administrator
                    </p>
                    <p className="text-sm text-foreground mt-1 whitespace-pre-wrap break-words">
                      {reason}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="rounded-md border border-border bg-muted/40 p-4 mb-6 text-left">
              <div className="flex items-start gap-3">
                <Mail className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-foreground">
                    Think this is a mistake?
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Please contact support and we'll take a look:
                  </p>
                  <a
                    href={`mailto:${SUPPORT_EMAIL}`}
                    className="mt-2 inline-block text-sm font-medium text-primary hover:underline break-all"
                  >
                    {SUPPORT_EMAIL}
                  </a>
                </div>
              </div>
            </div>

            <Link
              to="/"
              className="inline-block text-sm text-muted-foreground hover:text-foreground hover:underline"
            >
              Back to homepage
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
